/** Format ISO datetime for display (e.g. 17/03/2026, 15:40) */
export function formatClientHealthDateTime(iso) {
  if (!iso || typeof iso !== "string") return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

/** Normalize API `message` (string or object) for display in tooltips. */
function formatMessageForHover(message) {
  if (message == null || message === "") return "";
  if (typeof message === "string") return message.trim();
  if (typeof message === "object") {
    const text = typeof message.message === "string" ? message.message.trim() : "";
    const code = message.errorCode != null && message.errorCode !== "" ? String(message.errorCode) : "";
    if (text && code) return `${text} (${code})`;
    if (text) return text;
    if (code) return code;
    try {
      return JSON.stringify(message);
    } catch {
      return "";
    }
  }
  return String(message);
}

function buildHoverDetail(endpoint) {
  if (!endpoint || typeof endpoint !== "object") return null;
  const typeLine = endpoint.error_type ? `Error type: ${endpoint.error_type}` : "";
  const msgLine = formatMessageForHover(endpoint.message);
  if (!typeLine && !msgLine) return null;
  if (typeLine && msgLine) return `${typeLine}\n\n${msgLine}`;
  return typeLine || msgLine;
}

/**
 * @returns {{ variant: 'empty'|'success'|'failed', label: string|null, lastFormatted: string, hoverText: string|null }}
 */
export function parseEndpointHealth(endpoint) {
  if (!endpoint || typeof endpoint !== "object") {
    return { variant: "empty", label: null, lastFormatted: "—", hoverText: null };
  }

  const lastFormatted = formatClientHealthDateTime(endpoint.last_success_at);

  if (endpoint.status === true) {
    return { variant: "success", label: "SUCCESS", lastFormatted, hoverText: null };
  }

  if (endpoint.status === false) {
    const hoverText = buildHoverDetail(endpoint);
    return { variant: "failed", label: "FAILED", lastFormatted, hoverText };
  }

  return { variant: "empty", label: null, lastFormatted, hoverText: null };
}

const ENDPOINT_KEYS = ["order_book", "trade_book", "position_book", "holding_book"];

export function getRowHealthCells(row) {
  const status = row.last_endpoint_status && typeof row.last_endpoint_status === "object" ? row.last_endpoint_status : {};
  return ENDPOINT_KEYS.map((key) => ({
    key,
    ...parseEndpointHealth(status[key]),
  }));
}

/** Lowercase string of all searchable fields on a client health row (for universal filter). */
export function getClientHealthRowSearchText(row) {
  if (!row || typeof row !== "object") return "";
  const parts = [row.username, row.master_username, row.broker_name, row.broker_client_code, row.full_name];
  const st = row.last_endpoint_status;
  if (st && typeof st === "object") {
    for (const ep of Object.values(st)) {
      if (!ep || typeof ep !== "object") continue;
      if (ep.error_type) parts.push(ep.error_type);
      const msg = ep.message;
      if (typeof msg === "string") parts.push(msg);
      else if (msg && typeof msg === "object") {
        parts.push(msg.message, msg.errorCode, msg.data);
      }
    }
  }
  for (const c of getRowHealthCells(row)) {
    if (c.label) parts.push(c.label);
    if (c.hoverText) parts.push(c.hoverText.replace(/\s+/g, " "));
  }
  return parts.map((v) => String(v ?? "").toLowerCase()).join(" ");
}
